import React, { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import { connectVairAccount } from 'services/client/vairSignIn';
import { VairConnection } from 'context/CurrentUserContext';
import { useModal } from 'hooks/useModal';
import VairConnectionModal from 'components/modals/VairConnectionModal/VairConnectionModal';
import VairConnectionStatusModal from 'components/modals/VairConnectionStatusModal/VairConnectionStatusModal';
import { useGetCurrentUser } from './useGetCurrentUser';
import { useViewer } from './useViewer';

interface UseVairConnectionReturn {
  connection: VairConnection | undefined;
  loading: boolean;
  called: boolean;
  isConnected: boolean;
  connectToVair: (returnTo?: string) => void;
  refetch: () => void;
  openVairConnectionModal: (action?: 'create' | 'join') => void;
  VairConnectionStatusModal: React.ReactElement | null;
  VairConnectionModal: React.ReactElement | null;
}

export const useVairConnection = (): UseVairConnectionReturn => {
  const router = useRouter();
  const viewer = useViewer();
  const { user, loading, called, refetch } = useGetCurrentUser();
  const vairConnection = user?.userVairConnection ?? undefined;
  const isVairConnected = !!vairConnection;
  const refreshVairConnection = React.useCallback(() => {
    if (viewer.userId) refetch?.();
  }, [viewer.userId, refetch]);

  const {
    isOpen: isInfoModalOpen,
    openModal: openInfoModal,
    closeModal: closeInfoModal,
  } = useModal();
  const {
    isOpen: isConnectionModalOpen,
    openModal: openConnectionModal,
    closeModal: closeConnectionModal,
  } = useModal();
  const [connectionAction, setConnectionAction] = useState<'create' | 'join' | undefined>();
  const [modalType, setModalType] = useState<'success_login' | 'error' | null>(null);

  const connection = vairConnection;
  const isConnected = isVairConnected;

  // Single unified entry point: VAIR's consent page handles both sign-in and
  // account creation, so connecting is one redirect — no separate signup flow.
  const connectToVair = useCallback(
    (returnTo?: string) => {
      if (!viewer.userId) return;

      // The route derives the account from the auth token, so a failure here means the
      // session is gone — surface it rather than leaving the button looking inert.
      connectVairAccount(returnTo).catch((connectError) => {
        Sentry.captureException(connectError, { extra: { context: 'useVairConnection:connect' } });
        setModalType('error');
        openInfoModal();
      });
    },
    [viewer.userId, openInfoModal],
  );

  // Handle query params for success/error modals
  useEffect(() => {
    if (!router.isReady) return;

    const vairSuccess = router.query.vair_success;
    const error = router.query.error;

    if (vairSuccess === 'true') {
      // User successfully connected
      setModalType('success_login');
      openInfoModal();
      refreshVairConnection();
      // Remove query params - use asPath to preserve dynamic route segments
      const pathWithoutQuery = router.asPath.split('?')[0];
      router.replace(pathWithoutQuery, undefined, { shallow: true });
    } else if (error === 'already_connected') {
      setModalType('error');
      openInfoModal();
      // Remove query params - use asPath to preserve dynamic route segments
      const pathWithoutQuery = router.asPath.split('?')[0];
      router.replace(pathWithoutQuery, undefined, { shallow: true });
    }
  }, [
    router.isReady,
    router.query.vair_success,
    router.query.error,
    router.asPath,
    router,
    openInfoModal,
  ]);

  const handleCloseInfoModal = useCallback(() => {
    closeInfoModal(false);
    setModalType(null);
  }, [closeInfoModal]);

  const handleCloseConnectionModal = useCallback(() => {
    closeConnectionModal(false);
  }, [closeConnectionModal]);

  const handleConnect = useCallback(() => {
    closeConnectionModal(false);
    const returnTo =
      typeof window !== 'undefined' ? window.location.pathname + window.location.search : '';
    connectToVair(returnTo);
  }, [closeConnectionModal, connectToVair]);

  return {
    connection,
    loading,
    called,
    isConnected,
    connectToVair,
    refetch: refreshVairConnection,
    openVairConnectionModal: (action?: 'create' | 'join') => {
      setConnectionAction(action);
      openConnectionModal();
    },
    VairConnectionStatusModal: modalType ? (
      <VairConnectionStatusModal
        isOpen={isInfoModalOpen}
        onClose={handleCloseInfoModal}
        type={modalType}
      />
    ) : null,
    VairConnectionModal: (
      <VairConnectionModal
        isOpen={isConnectionModalOpen}
        action={connectionAction}
        onClose={handleCloseConnectionModal}
        onConnect={handleConnect}
      />
    ),
  };
};
